import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageCircle, Palette, Ruler, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProductById, subCategories, getRelatedProducts } from '../data/products';
import { useTheme } from '../context/ThemeContext';
import { Helmet } from "react-helmet-async";

<Helmet>
  <title>Our Products | Hotel, Hospital & School Uniform Collection</title>
  <meta name="description" content="Explore our range of hotel staff uniforms, hospital wear, and school uniforms designed with premium fabrics and long-lasting quality." />
  <meta name="keywords" content="hotel uniforms design, hospital uniforms supplier, school uniforms India, uniform products" />

  <meta property="og:title" content="Premium Uniform Manufacturer in India" />
  <meta property="og:description" content="High-quality uniforms for hotels, hospitals, and schools." />
  <meta property="og:type" content="website" />
</Helmet>

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

export const ProductDetail = () => {
  const { isDarkMode } = useTheme();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const foundProduct = getProductById(id);
    if (foundProduct) {
      setProduct(foundProduct);
      setRelatedProducts(getRelatedProducts(id, 3));
      setSelectedImageIndex(0);
    }
  }, [id]);

  const nextImage = () => {
    if (product) {
      setSelectedImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product) {
      setSelectedImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  if (!product) {
    return (
      <div className={`pt-24 min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-zinc-900' : ''}`}>
        <div className="text-center">
          <p className={`mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Design not found</p>
          <Link to="/products" className={`btn-secondary ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-black' : ''}`}>
            Back to Designs
          </Link>
        </div>
      </div>
    );
  }

  const whatsappMessage = encodeURIComponent(
    `Hello MALABISthestic! I'm interested in the "${product.name}" design.\n\nI would like to customize it as per my measurement and colour preference.\n\nPlease share more details.`
  );
  const whatsappUrl = `https://wa.me/916398130510?text=${whatsappMessage}`;

  return (
    <div className={`pt-24 md:pt-28 min-h-screen ${isDarkMode ? 'bg-zinc-900' : ''}`} data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="container-custom py-6">
        <Link 
          to="/products" 
          className={`inline-flex items-center gap-2 text-sm hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
          data-testid="back-to-products"
        >
          <ArrowLeft size={16} />
          Back to Designs
        </Link>
      </div>

      {/* Product Section */}
      <section className="pb-16 md:pb-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Product Images Gallery - 4-5 images */}
            <motion.div
              className="relative"
              initial="initial"
              animate="animate"
              variants={fadeUp}
            >
              {/* Main Image */}
              <div className={`aspect-[3/4] overflow-hidden relative group ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-100'}`}>
                <img
                  src={product.images[selectedImageIndex]}
                  alt={`${product.name} - View ${selectedImageIndex + 1}`}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
                
                {/* Navigation Arrows */}
                {product.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                      aria-label="Next image"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1">
                  {selectedImageIndex + 1} / {product.images.length}
                </div>
              </div>

              {/* Thumbnail Gallery */}
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-24 overflow-hidden border-2 transition-all ${
                      selectedImageIndex === index 
                        ? 'border-[#D4AF37]' 
                        : isDarkMode ? 'border-zinc-700 hover:border-zinc-500' : 'border-gray-200 hover:border-gray-400'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Category Badge */}
              <div className={`absolute top-4 left-4 px-4 py-2 ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                <span className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-white' : ''}`}>
                  {product.category === 'hotel' ? 'Hospitality' : 'Healthcare'}
                </span>
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div
              className="lg:py-8"
              initial="initial"
              animate="animate"
              variants={fadeUp}
            >
              {/* Category & Sub */}
              <p className={`text-xs uppercase tracking-widest mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {product.category === 'hotel' ? 'Hotel Staff' : 'Hospital Staff'} / {
                  subCategories[product.category]?.find(s => s.id === product.subCategory)?.name
                }
              </p>

              {/* Title */}
              <h1 
                className={`text-3xl md:text-4xl mb-6 ${isDarkMode ? 'text-white' : ''}`}
                style={{ fontFamily: 'Bodoni Moda, serif' }}
                data-testid="product-title"
              >
                {product.name}
              </h1>

              {/* Gold Line */}
              <div className="gold-line mb-6" />

              {/* Description */}
              <p className={`leading-relaxed mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} data-testid="product-description">
                {product.description}
              </p>

              {/* Customization Notice */}
              <div className={`p-6 mb-8 border ${isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-[#fdfbf5] border-[#D4AF37]/20'}`}>
                <h3 className={`text-lg mb-4 ${isDarkMode ? 'text-white' : ''}`} style={{ fontFamily: 'Bodoni Moda, serif' }}>
                  We Deal in Customization
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                      <Ruler size={18} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>As Per Your Measurement</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Tailored to your exact specifications</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                      <Palette size={18} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>As Per Your Selected Colour</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Choose your preferred colour combination</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-8">
                <h3 className={`text-xs uppercase tracking-widest font-medium mb-4 ${isDarkMode ? 'text-gray-300' : ''}`}>Features</h3>
                <ul className="grid grid-cols-1 gap-2">
                  {product.features.map((feature) => (
                    <li key={feature} className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary flex items-center justify-center gap-3"
                  data-testid="inquiry-whatsapp"
                >
                  <MessageCircle size={18} />
                  Customize This Design
                </a>
                <Link to="/contact" className={`btn-secondary text-center ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-black' : ''}`} data-testid="contact-link">
                  Send Inquiry
                </Link>
              </div>

              {/* Contact Info */}
              <div className={`mt-10 pt-8 border-t ${isDarkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
                <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Contact us for customization:</p>
                <div className="flex flex-wrap gap-6">
                  <a href="tel:+916398130510" className={`text-sm hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-gray-300' : ''}`}>
                    +91 6398130510
                  </a>
                  <a href="tel:+917037738809" className={`text-sm hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-gray-300' : ''}`}>
                    +91 7037738809
                  </a>
                  <a href="mailto:theisticmalabis@gmail.com" className={`text-sm hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-gray-300' : ''}`}>
                    theisticmalabis@gmail.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className={`py-16 md:py-24 ${isDarkMode ? 'bg-zinc-800' : 'bg-[#f9fafb]'}`}>
          <div className="container-custom">
            <motion.div
              className="mb-12"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>More Designs in {subCategories[product.category]?.find(s => s.id === product.subCategory)?.name}</p>
              <h2 className={`section-title ${isDarkMode ? 'text-white' : ''}`}>Related Designs</h2>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link
                    to={`/products/${item.id}`}
                    className="product-card block group"
                    data-testid={`related-${item.id}`}
                  >
                    <div className={`overflow-hidden aspect-[3/4] ${isDarkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className={`p-4 ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                      <h3 
                        className={`text-sm md:text-base group-hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-white' : ''}`}
                        style={{ fontFamily: 'Bodoni Moda, serif' }}
                      >
                        {item.name}
                      </h3>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Customizable Design
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
